import { get, cloneDeep, isString, isArray, merge } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import RefreshIcon from '@mui/icons-material/Refresh'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { Box, Button, Grid, IconButton, MenuItem, Popover, Select, Typography } from '@mui/material'

import { Criteria } from '.'
import { KeyValPair } from '@models/common.model'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { FormAutocomplete } from '@components/configurations/form/autocomplete'
import { FormTextField } from '@components/configurations/form/textfield'
import { useSnackbar } from 'notistack'
import { FormSelect } from '@components/configurations/form/select'

export const FiltersPopover = ({
	criterion,
	defaultFilterCriteria,
	onSubmit,
	filter,
	setFilter,
}: {
	criterion?: Criteria[]
	defaultFilterCriteria?: any
	onSubmit: (filter: {}) => void
	filter: any
	setFilter: any
}) => {
	const { t } = useTranslation()
	const { enqueueSnackbar } = useSnackbar()
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

	const contains = { key: '~', value: 'including' }
	const notContains = { key: '!~', value: 'excluding' }
	const equals = { key: '=', value: 'including' }
	const notEqualsTo = { key: '!=', value: 'excluding' }

	const getOperatorByType = (type: string | undefined): KeyValPair[] => {
		switch (type) {
			case 'SelectionFilter':
				return [equals, notEqualsTo]
			case 'StringFilter':
				return [contains, notContains]
			default:
				return []
		}
	}

	const defaultValues = criterion?.reduce(
		(a, b) => ({
			...a,
			[b.key]: {
				operator: b.type ? getOperatorByType(b.type)?.[0]?.key || '' : '',
				value:
					b.type === 'SelectionFilter' || b.type === 'ActiveFilter'
						? []
						: b.type === 'StringFilter'
						? ''
						: undefined,
			},
		}),
		defaultFilterCriteria ?? {},
	) as any

	const methods = useForm(defaultValues)

	const { setValue, getValues, reset, control } = methods

	const handleSubmit = () => {
		console.log('handleSubmit getValues', getValues())

		const _filter = cloneDeep(getValues())
		Object.keys(_filter).map((key) => {
			const { operator, value } = get(_filter, key, {})

			const criteria = criterion?.find((crit) => crit.key === key)
			const { type, name } = criteria ?? {}

			switch (type) {
				case 'SelectionFilter':
					if (Array.isArray(value) && value.length === 0) {
						delete _filter[key]
					}
					break
				case 'StringFilter':
					if (value === undefined || value === '') {
						delete _filter[key]
					}
					break
				case 'ActiveFilter':
					if (!operator || (Array.isArray(value) && value?.length === 0)) {
						delete _filter[key]
					}
					break
			}
		})
		setFilter(_filter)
		onSubmit(_filter)
		setAnchorEl(null)
	}

	useEffect(() => {
		reset(filter)
	}, [filter])

	const open = Boolean(anchorEl)
	const id = open ? 'simple-popover' : undefined

	const operatorField = (criteria: Criteria) => {
		const operatorList = getOperatorByType(criteria.type)
		return (
			<FormSelect
				name={`${criteria.key}.operator`}
				control={control}
				label={''}
				options={operatorList ?? []}
				defaultValue={operatorList[0].key}
				isOperatorField={true}
			/>
		)
	}

	const valueField = (criteria: Criteria) => {
		switch (criteria.type) {
			case 'StringFilter':
				return <FormTextField control={control} name={`${criteria.key}.value`} label="" fullWidth />
			case 'SelectionFilter':
				return (
					<FormAutocomplete
						key={`${criteria.name}-value`}
						control={control}
						name={`${criteria.key}.value`}
						label={''}
						optionList={criteria.options ?? []}
						multiple
					/>
				)
		}
	}

	// const watchFilters = useWatch({ control })

	// const handleDisable = () => {
	// 	const _filter = cloneDeep(watchFilters)
	// 	console.log(_filter)
	// 	const validationList = []
	// 	for (let item in _filter) {
	// 		const operator = _filter[item]['operator']
	// 		const value = _filter[item]['value']

	// 		switch (item) {
	// 			case 'active':
	// 				let obj: any = {}
	// 				if (operator === '=' && value.length) {
	// 					obj[item] = false
	// 					validationList.push(obj)
	// 				} else {
	// 					obj[item] = true
	// 					validationList.push(obj)
	// 				}
	// 				break
	// 			case 'email':
	// 			case 'name':
	// 			case 'phone':
	// 			case 'position':
	// 				let obj2: any = {}
	// 				if ((operator === '~' || operator === '!~') && value) {
	// 					obj2[item] = false
	// 					validationList.push(obj2)
	// 				} else if (operator === '' && value === '') {
	// 					obj2[item] = false
	// 					validationList.push(obj2)
	// 				} else if (operator?.length === 0 && value === undefined) {
	// 					obj2[item] = false
	// 					validationList.push(obj2)
	// 				} else {
	// 					obj2[item] = true
	// 					validationList.push(obj2)
	// 				}
	// 				break
	// 			case 'org_ids':
	// 			case 'project_ids':
	// 				let obj3: any = {}
	// 				if ((operator === '=' || operator === '!=') && value.length) {
	// 					obj3[item] = false
	// 					validationList.push(obj3)
	// 				} else if (operator?.length === 0 && value?.length === 0) {
	// 					obj3[item] = false
	// 					validationList.push(obj3)
	// 				} else {
	// 					obj3[item] = true
	// 					validationList.push(obj3)
	// 				}
	// 				break
	// 		}
	// 	}
	// 	console.log('validationList: ', validationList)
	// 	return validationList.some((ele) => {
	// 		if (Object.values(ele)[0]) {
	// 			return true
	// 		}
	// 	})
	// }

	return (
		<Box>
			<IconButton
				onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
					setAnchorEl(event.currentTarget)
				}}
				sx={{
					backgroundColor: '#6C91A5',
					borderRadius: '10px',
					marginLeft: '10px',
					'&:hover': {
						backgroundColor: '#304A58',
					},
				}}
			>
				<FilterAltIcon color="white" />{' '}
			</IconButton>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				PaperProps={{ style: { backgroundColor: 'white', minWidth: '500px', maxWidth: '500px' } }}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						backgroundColor: '#6C91A5',
					}}
				>
					<IconButton
						onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
							defaultValues.active = { operator: '=', value: [true, false] }
							reset(defaultValues)
						}}
					>
						<RefreshIcon color="white" />
					</IconButton>
					<IconButton
						onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
							setAnchorEl(null)
						}}
					>
						<FilterAltIcon color="white" />{' '}
					</IconButton>
				</Box>
				{criterion?.map((criteria, index) => (
					<Box sx={{ maxHeight: '700px' }}>
						<Grid container sx={{ paddingX: 2, paddingY: 0.5 }}>
							<Grid item lg={12}>
								<Typography variant="h6">{t(criteria?.name)}</Typography>
							</Grid>
						</Grid>
						{criteria.type === 'ActiveFilter' ? (
							<Grid
								container
								key={`${criteria.key}-filter-wrapper`}
								sx={{ paddingX: 2, paddingY: 0.5, alignItems: 'flex-end' }}
							>
								<Grid item lg={4} alignItems="flex-end" sx={{ paddingRight: 1 }}>
									<FormAutocomplete
										key={`${criteria.name}.value`}
										control={control}
										name={`${criteria.key}.value`}
										label={''}
										optionList={[
											{ key: [true], value: 'Activated' },
											{ key: [false], value: 'Deactivated' },
											{ key: [true, false], value: 'All' },
										]}
										additionChangeAction={(newValue: KeyValPair | KeyValPair[] | null) => {
											if (!isArray(newValue)) {
												setValue(`${criteria.key}.operator`, '=')
											}
										}}
										canDisabledClear={true}
									/>
								</Grid>
							</Grid>
						) : (
							<Grid
								container
								key={`${criteria.key}-filter-wrapper`}
								sx={{ paddingX: 2, paddingY: 0.5, alignItems: 'center'}}
							>
								<Grid item lg={4} alignItems="flex-end" sx={{ paddingRight: 1 }}>
									{operatorField(criteria)}
								</Grid>
								<Grid item lg={8}>
									{valueField(criteria)}
								</Grid>
							</Grid>
						)}
					</Box>
				))}
				<Grid container sx={{ padding: 1, alignItems: 'baseline' }}>
					<Grid item lg={8}></Grid>
					<Grid item lg={2}></Grid>
					<Grid item lg={2}>
						<Button variant="contained" color="defaultButton" onClick={handleSubmit}>
							Apply
						</Button>
					</Grid>
				</Grid>
			</Popover>
		</Box>
	)
}
